import React, { useCallback } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import { useAppState } from '@sg/garnish';

import { Bag } from '@order/features/ordering';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { useIsLoggedIn } from '../../context/AuthMachine';
import { useGlobalAppState } from '../../context/GlobalAppState';
import { useCloseBag } from '../../hooks';
import {
  BagAnimatedDialog,
  BagAnimatedDialogWithStripe,
  BagContent,
} from './components';
import { useBagGestures, useOrderState } from './hooks';
import { getOrderPlaybackConfig } from './utils';

export const BagScreen = () => {
  const isTimeslotUnavailableModalEnabled = useFeatureFlag('CELS-4552-timeslot-unavailable-modal-enabled'); // prettier-ignore
  const {
    dynamicFeatureFlags: {
      orderPlaybackConfig,
      isExpressPaymentsEnabled,
      isOrderPlaybackEnabled,
      isStripePaymentsSdkEnabled,
    },
  } = useGlobalAppState();

  const parsedPlaybackConfig = getOrderPlaybackConfig(orderPlaybackConfig);
  const { lineItemsFadeIn, confirmationFadeIn } = parsedPlaybackConfig;

  const orderSubmissionDelay = isOrderPlaybackEnabled
    ? lineItemsFadeIn + confirmationFadeIn
    : 0;

  const orderState = useOrderState({
    isTimeslotUnavailableModalEnabled,
    isStripePaymentsSdkEnabled,
    isExpressPaymentsEnabled,
    isOrderPlaybackEnabled,
    orderSubmissionDelay,
  });

  const {
    isFetchingInitialData,
    isSubmittingOrder,
    isShowingModal,
    refetchWantedTimes,
    startOrdering,
  } = orderState;

  const isLoggedIn = useIsLoggedIn();
  const shouldRenderAnimatedDialog = isLoggedIn && !isFetchingInitialData;

  // ──────────────────────────────────────────────────────────────────────────

  const closeBag = useCloseBag();

  // Prevent closing bag while submitting an order.
  const handleCloseBag = useCallback(() => {
    if (isSubmittingOrder) return;

    closeBag();
  }, [isSubmittingOrder, closeBag]);

  // Enable/disable gestures when modal is shown.
  useBagGestures(isShowingModal);

  // Fetch ordering data on bag focus.
  useFocusEffect(startOrdering);

  // Refetch wanted times on app focus.
  useAppState({ onVisible: refetchWantedTimes });

  // ──────────────────────────────────────────────────────────────────────────

  const AnimatedDialog = isStripePaymentsSdkEnabled
    ? BagAnimatedDialogWithStripe
    : BagAnimatedDialog;

  return (
    <Bag.DynamicModal onClose={handleCloseBag}>
      <BagContent orderState={orderState} />

      {shouldRenderAnimatedDialog ? (
        <AnimatedDialog
          orderState={orderState}
          isOrderPlaybackEnabled={isOrderPlaybackEnabled}
          orderPlaybackConfig={parsedPlaybackConfig}
        />
      ) : null}
    </Bag.DynamicModal>
  );
};
